// src/pages/BookMaintenance.jsx
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../services/supabaseClient';
import { openRazorpayCheckout } from '../services/razorpay';

const PACKAGES = [
  {
    name: 'Basic', range: '1-10 pots', price: 600, display: '₹600', tagline: 'Perfect for small balconies',
    features: ['Soil loosening and aeration', 'Fertilizer application', 'Light pruning and cleaning', 'Basic pest inspection'],
  },
  {
    name: 'Standard', range: '11-20 pots', price: 900, display: '₹900', tagline: 'For medium plant collections',
    features: ['All Basic services', 'Deep pruning and shaping', 'Detailed pest check and treatment', 'Plant health consultation', 'Care tips for each plant'],
  },
  {
    name: 'Premium', range: '21+ pots', price: 1200, display: '₹1200', tagline: 'Complete plant care solution',
    features: ['All Standard services', 'Repotting assistance', 'Customized care plan', 'Before/after photos', 'Follow-up visit within 2 weeks', 'WhatsApp support for 1 month'],
  },
];

const TIME_SLOTS = [
  '10:00 AM - 11:00 AM',
  '11:00 AM - 12:00 PM',
  '12:00 PM - 01:00 PM',
  '02:00 PM - 03:00 PM',
  '03:00 PM - 04:00 PM',
  '04:00 PM - 05:00 PM',
  '05:00 PM - 06:00 PM',
];

const STEPS = ['Select Package', 'Your Details', 'Confirmation'];

// ── Send email helper ──────────────────────────────────────────
const sendBookingEmails = async (bookingData) => {
  try {
    // Email to customer
    await supabase.functions.invoke('send-email', {
      body: {
        type: 'booking_confirmation',
        to:   bookingData.email,
        data: {
          name:           bookingData.name,
          phone:          bookingData.phone,
          package:        bookingData.package,
          preferred_date: new Date(bookingData.preferred_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' }),
          preferred_time: bookingData.preferred_time,
          num_pots:       bookingData.num_pots,
          amount:         bookingData.amount,
        },
      },
    });

    // Email to admin
    await supabase.functions.invoke('send-email', {
      body: {
        type: 'booking_admin',
        to:   'support@balconygreen.in',
        data: {
          name:           bookingData.name,
          phone:          bookingData.phone,
          address:        bookingData.address,
          package:        bookingData.package,
          preferred_date: new Date(bookingData.preferred_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' }),
          preferred_time: bookingData.preferred_time,
          num_pots:       bookingData.num_pots,
          amount:         bookingData.amount,
        },
      },
    });
  } catch (err) {
    // Email failure should not block booking
    console.error('Email sending failed:', err);
  }
};

export default function BookMaintenance() {
  const [step,        setStep]        = useState(1);
  const [selectedPkg, setSelectedPkg] = useState(null);
  const [form,        setForm]        = useState({
    name: '', phone: '', email: '', address: '', num_pots: '', preferred_date: '', preferred_time: '',
  });
  const [loading,     setLoading]     = useState(false);
  const [payLoading,  setPayLoading]  = useState(false);
  const [error,       setError]       = useState('');
  const [bookingId,   setBookingId]   = useState(null);
  const [paymentDone, setPaymentDone] = useState(false);
  const navigate = useNavigate();

  const handleChange = e => {
    setForm(f => ({ ...f, [e.target.name]: e.target.value }));
    setError('');
  };

  const handleContinue = () => {
    if (!selectedPkg) { setError('Please select a package to continue.'); return; }
    setError(''); setStep(2);
  };

  // ── Save booking to Supabase ───────────────────────────────
  const handleSubmit = async e => {
    e.preventDefault();
    if (!form.name.trim())            { setError('Name is required.'); return; }
    if (!/^\d{10}$/.test(form.phone)) { setError('Enter a valid 10-digit phone number.'); return; }
    if (!form.email.trim())           { setError('Email is required.'); return; }
    if (!/\S+@\S+\.\S+/.test(form.email)) { setError('Enter a valid email address.'); return; }
    if (!form.address.trim())         { setError('Address is required.'); return; }
    if (!form.num_pots || Number(form.num_pots) < 1) { setError('Number of pots must be at least 1.'); return; }
    if (!form.preferred_date)         { setError('Please pick a preferred date.'); return; }
    if (!form.preferred_time)         { setError('Please select a preferred time slot.'); return; }

    setLoading(true); setError('');
    try {
      const { data, error: sbErr } = await supabase.from('bookings').insert([{
        name:           form.name.trim(),
        phone:          form.phone,
        address:        form.address.trim(),
        num_pots:       Number(form.num_pots),
        preferred_date: form.preferred_date,
        preferred_time: form.preferred_time,
        status:         'Pending',
        package:        selectedPkg.name,
      }]).select();
      if (sbErr) throw sbErr;

      const newBookingId = data?.[0]?.id || null;
      setBookingId(newBookingId);

      // Send emails after successful booking
      await sendBookingEmails({
        name:           form.name.trim(),
        phone:          form.phone,
        email:          form.email.trim(),
        address:        form.address.trim(),
        package:        selectedPkg.name,
        preferred_date: form.preferred_date,
        preferred_time: form.preferred_time,
        num_pots:       form.num_pots,
        amount:         selectedPkg.display,
      });

      setStep(3);
    } catch (err) {
      console.error(err);
      setError('Failed to submit booking. Please check your Supabase configuration and try again.');
    } finally {
      setLoading(false);
    }
  };

  // ── Razorpay payment ───────────────────────────────────────
  const handlePayNow = async () => {
    setPayLoading(true); setError('');
    try {
      await openRazorpayCheckout({
        amount:      selectedPkg.price,
        description: `${selectedPkg.name} Package - Balcony Maintenance`,
        receipt:     `bkg_${Date.now()}`.slice(0, 40),
        notes: {
          type:       'maintenance',
          package:    selectedPkg.name,
          customer:   form.name,
          phone:      form.phone,
          booking_id: bookingId || '',
        },
        prefill: {
          name:    form.name,
          contact: form.phone,
          email:   form.email,
        },
        onSuccess: async (response, order) => {
          if (bookingId) {
            await supabase.from('bookings').update({ status: 'Confirmed' }).eq('id', bookingId);
          }
          setPaymentDone(true);
          setPayLoading(false);
        },
        onFailure: (msg) => {
          setError(msg === 'Payment cancelled by user.' ? '' : `Payment failed: ${msg}`);
          setPayLoading(false);
        },
      });
    } catch (err) {
      setError(`Payment error: ${err.message}`);
      setPayLoading(false);
    }
  };

  const resetForm = () => {
    setStep(1);
    setSelectedPkg(null);
    setForm({ name: '', phone: '', email: '', address: '', num_pots: '', preferred_date: '', preferred_time: '' });
    setPaymentDone(false);
    setBookingId(null);
    setError('');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Location bar */}
      <div className="bg-green-50 border-b border-green-100 text-center py-2 text-sm text-gray-600 mt-16">
        Serving <span className="font-semibold text-gray-800">Ulwe, Navi Mumbai - 410206</span>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-12">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2">Book Maintenance Service</h1>
          <p className="text-gray-500">Professional plant care for your balcony</p>
        </div>

        {/* Step indicator */}
        <div className="flex items-center justify-center gap-0 mb-10">
          {STEPS.map((label, i) => {
            const num = i + 1; const active = step === num; const done = step > num;
            return (
              <div key={label} className="flex items-center">
                <div className="flex flex-col items-center">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold border-2 transition-all
                    ${done ? 'bg-[#2e7d32] border-[#2e7d32] text-white' : active ? 'border-[#2e7d32] text-[#2e7d32]' : 'border-gray-300 text-gray-400'}`}>
                    {done ? '✓' : num}
                  </div>
                  <span className={`text-xs mt-1 font-medium ${active || done ? 'text-[#2e7d32]' : 'text-gray-400'}`}>{label}</span>
                </div>
                {i < STEPS.length - 1 && (
                  <div className={`w-16 sm:w-24 h-0.5 mb-4 mx-1 ${step > num ? 'bg-[#2e7d32]' : 'bg-gray-200'}`} />
                )}
              </div>
            );
          })}
        </div>

        {/* ── STEP 1: Select Package ── */}
        {step === 1 && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 sm:p-8">
            <h2 className="text-xl font-bold text-gray-900 mb-6">Step 1: Select Your Package</h2>
            {error && <div className="mb-4 px-4 py-3 bg-red-50 border border-red-200 text-red-700 rounded-xl text-sm">{error}</div>}
            <div className="grid sm:grid-cols-3 gap-4 mb-6">
              {PACKAGES.map(pkg => {
                const selected = selectedPkg?.name === pkg.name;
                const visible  = pkg.features.slice(0, 3);
                const extra    = pkg.features.length - 3;
                return (
                  <div key={pkg.name} onClick={() => { setSelectedPkg(pkg); setError(''); }}
                    className={`cursor-pointer rounded-xl border-2 p-5 transition-all duration-200
                      ${selected ? 'border-[#2e7d32] bg-green-50' : 'border-gray-200 hover:border-gray-300'}`}>
                    <div className="flex justify-between items-start mb-1">
                      <h3 className="font-bold text-gray-900 text-lg">{pkg.name}</h3>
                      {selected && (
                        <div className="w-5 h-5 rounded-full bg-[#2e7d32] flex items-center justify-center">
                          <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" className="w-3 h-3">
                            <polyline points="20 6 9 17 4 12"/>
                          </svg>
                        </div>
                      )}
                    </div>
                    <p className="text-gray-500 text-sm mb-3">{pkg.range} pots</p>
                    <p className="text-2xl font-bold text-[#2e7d32] mb-3">{pkg.display}</p>
                    <p className="text-gray-500 text-xs mb-4">{pkg.tagline}</p>
                    <ul className="space-y-1.5">
                      {visible.map(f => (
                        <li key={f} className="flex items-start gap-2 text-xs text-gray-600">
                          <span className="text-[#2e7d32] font-bold">✓</span>{f}
                        </li>
                      ))}
                      {extra > 0 && <li className="text-xs text-gray-400 mt-1">+ {extra} more</li>}
                    </ul>
                  </div>
                );
              })}
            </div>
            <button onClick={handleContinue}
              className={`w-full py-4 rounded-xl font-semibold text-base transition-all duration-200
                ${selectedPkg ? 'bg-[#2e7d32] text-white hover:bg-[#1b5e20]' : 'bg-gray-300 text-gray-500 cursor-not-allowed'}`}>
              Continue
            </button>
          </div>
        )}

        {/* ── STEP 2: Your Details ── */}
        {step === 2 && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 sm:p-8">
            <div className="flex items-center gap-3 mb-6">
              <button onClick={() => setStep(1)} className="text-gray-400 hover:text-gray-600">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5">
                  <polyline points="15 18 9 12 15 6"/>
                </svg>
              </button>
              <h2 className="text-xl font-bold text-gray-900">Step 2: Your Details</h2>
            </div>

            {/* Selected package summary */}
            <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-6 flex justify-between items-center">
              <div>
                <span className="text-sm font-semibold text-gray-700">Selected: </span>
                <span className="text-sm font-bold text-[#2e7d32]">{selectedPkg.name} Package — {selectedPkg.display}</span>
              </div>
              <button onClick={() => setStep(1)} className="text-xs text-[#2e7d32] underline">Change</button>
            </div>

            {/* ── Disclaimer Note ── */}
            <div className="bg-blue-50 border border-blue-200 rounded-2xl p-5 mb-5">
              <div className="flex items-center gap-2 mb-3">
                <span className="text-base">📋</span>
                <h3 className="font-bold text-gray-900 text-sm">Please Note — Service Information</h3>
              </div>
              <ul className="space-y-2.5">
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-5 h-5 bg-green-100 rounded-full flex items-center justify-center mt-0.5">
                    <svg viewBox="0 0 24 24" fill="none" stroke="#2e7d32" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" className="w-3 h-3"><polyline points="20 6 9 17 4 12"/></svg>
                  </span>
                  <p className="text-xs text-gray-700"><span className="font-semibold text-gray-900">Basic fertilizer is included</span> in all service packages at no extra charge.</p>
                </li>
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-5 h-5 bg-red-100 rounded-full flex items-center justify-center mt-0.5">
                    <svg viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" className="w-3 h-3"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                  </span>
                  <p className="text-xs text-gray-700"><span className="font-semibold text-gray-900">Premium fertilizers and pesticides</span> are charged extra if required, based on plant condition.</p>
                </li>
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-5 h-5 bg-yellow-100 rounded-full flex items-center justify-center mt-0.5">
                    <svg viewBox="0 0 24 24" fill="none" stroke="#d97706" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" className="w-3 h-3"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                  </span>
                  <p className="text-xs text-gray-700"><span className="font-semibold text-gray-900">Large pots (18"+), terrace gardens, vertical gardens, and commercial spaces</span> may require a custom quote. <a href="tel:+917302472992" className="text-[#2e7d32] font-semibold underline">Call us</a></p>
                </li>
              </ul>
            </div>

            {error && <div className="mb-4 px-4 py-3 bg-red-50 border border-red-200 text-red-700 rounded-xl text-sm">{error}</div>}

            <form onSubmit={handleSubmit} noValidate className="space-y-4">
              {/* Name + Phone */}
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Full Name *</label>
                  <input name="name" type="text" value={form.name} onChange={handleChange}
                    placeholder="Priya Sharma"
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#2e7d32]/30 focus:border-[#2e7d32]" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Phone Number *</label>
                  <input name="phone" type="tel" value={form.phone} onChange={handleChange}
                    placeholder="9876543210" maxLength={10}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#2e7d32]/30 focus:border-[#2e7d32]" />
                </div>
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Email Address *</label>
                <input name="email" type="email" value={form.email} onChange={handleChange}
                  placeholder="your@email.com"
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#2e7d32]/30 focus:border-[#2e7d32]" />
                <p className="text-xs text-gray-400 mt-1">Booking confirmation will be sent to this email</p>
              </div>

              {/* Address */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Address *</label>
                <textarea name="address" rows={3} value={form.address} onChange={handleChange}
                  placeholder="Flat 4B, Greenview Apartments, Ulwe, Navi Mumbai"
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#2e7d32]/30 focus:border-[#2e7d32] resize-none" />
              </div>

              {/* Pots + Date */}
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Number of Pots *</label>
                  <input name="num_pots" type="number" value={form.num_pots} onChange={handleChange}
                    placeholder="5" min={1} max={500}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#2e7d32]/30 focus:border-[#2e7d32]" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Preferred Date *</label>
                  <input name="preferred_date" type="date" value={form.preferred_date} onChange={handleChange}
                    min={new Date().toISOString().split('T')[0]}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#2e7d32]/30 focus:border-[#2e7d32]" />
                </div>
              </div>

              {/* Time Slots */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Preferred Time Slot *</label>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                  {TIME_SLOTS.map(slot => (
                    <button key={slot} type="button"
                      onClick={() => { setForm(f => ({ ...f, preferred_time: slot })); setError(''); }}
                      className={`px-3 py-2.5 rounded-xl text-xs font-medium border-2 transition-all duration-150 text-center
                        ${form.preferred_time === slot
                          ? 'border-[#2e7d32] bg-green-50 text-[#2e7d32]'
                          : 'border-gray-200 text-gray-600 hover:border-gray-300'}`}>
                      {slot}
                    </button>
                  ))}
                </div>
                {form.preferred_time && (
                  <p className="text-xs text-[#2e7d32] mt-2 font-medium">✓ Selected: {form.preferred_time}</p>
                )}
              </div>

              <button type="submit" disabled={loading}
                className="w-full py-4 bg-[#2e7d32] text-white font-semibold rounded-xl hover:bg-[#1b5e20] transition-colors duration-200 disabled:opacity-60 mt-2">
                {loading ? 'Saving...' : 'Confirm Booking'}
              </button>
            </form>
          </div>
        )}

        {/* ── STEP 3: Confirmation + Payment ── */}
        {step === 3 && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 sm:p-10">

            {/* Success icon */}
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg viewBox="0 0 24 24" fill="none" stroke="#2e7d32" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="w-8 h-8">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-1">Booking Confirmed! 🎉</h2>
              <p className="text-gray-500 text-sm">A confirmation email has been sent to <span className="font-semibold text-[#2e7d32]">{form.email}</span></p>
            </div>

            {/* Booking summary */}
            <div className="bg-gray-50 rounded-xl p-5 mb-8 space-y-3">
              <h3 className="font-semibold text-gray-900 mb-3">Booking Summary</h3>
              {[
                ['Package',  `${selectedPkg?.name} — ${selectedPkg?.display}`],
                ['Name',     form.name],
                ['Phone',    form.phone],
                ['Email',    form.email],
                ['Date',     form.preferred_date ? new Date(form.preferred_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' }) : '—'],
                ['Time',     form.preferred_time],
                ['Pots',     form.num_pots],
              ].map(([label, value]) => (
                <div key={label} className="flex justify-between text-sm">
                  <span className="text-gray-500">{label}</span>
                  <span className="font-semibold text-gray-900 text-right max-w-[60%]">{value}</span>
                </div>
              ))}
              <div className="border-t border-gray-200 pt-3 flex justify-between text-sm">
                <span className="font-bold text-gray-900">Total Amount</span>
                <span className="font-bold text-[#2e7d32] text-lg">{selectedPkg?.display}</span>
              </div>
            </div>

            {/* Payment options */}
            {!paymentDone ? (
              <div>
                <h3 className="font-semibold text-gray-900 mb-4 text-center">Choose Payment Method</h3>
                {error && <div className="mb-4 px-4 py-3 bg-red-50 border border-red-200 text-red-700 rounded-xl text-sm">{error}</div>}

                <div className="grid sm:grid-cols-2 gap-4">
                  {/* Pay Online */}
                  <button onClick={handlePayNow} disabled={payLoading}
                    className="flex flex-col items-center gap-2 p-5 border-2 border-[#2e7d32] rounded-xl hover:bg-green-50 transition-all duration-200 disabled:opacity-60">
                    <div className="w-10 h-10 bg-[#2e7d32] rounded-full flex items-center justify-center">
                      <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5">
                        <rect x="1" y="4" width="22" height="16" rx="2" ry="2"/>
                        <line x1="1" y1="10" x2="23" y2="10"/>
                      </svg>
                    </div>
                    <span className="font-bold text-[#2e7d32]">{payLoading ? 'Opening...' : 'Pay Online Now'}</span>
                    <span className="text-xs text-gray-500">UPI, Card, Net Banking</span>
                    <span className="text-sm font-semibold text-gray-700">{selectedPkg?.display}</span>
                  </button>

                  {/* Pay at Service */}
                  <button onClick={() => setPaymentDone(true)}
                    className="flex flex-col items-center gap-2 p-5 border-2 border-gray-200 rounded-xl hover:bg-gray-50 transition-all duration-200">
                    <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                      <svg viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5">
                        <line x1="12" y1="1" x2="12" y2="23"/>
                        <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
                      </svg>
                    </div>
                    <span className="font-bold text-gray-700">Pay at Service</span>
                    <span className="text-xs text-gray-500">Cash or UPI on the day</span>
                    <span className="text-sm font-semibold text-gray-700">{selectedPkg?.display}</span>
                  </button>
                </div>
              </div>
            ) : (
              <div className="text-center">
                <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <svg viewBox="0 0 24 24" fill="none" stroke="#2e7d32" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="w-7 h-7">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                </div>
                <p className="font-bold text-gray-900 mb-1">🎉 All Done!</p>
                <p className="text-sm text-gray-500 mb-6">
                  Our team will contact you at <span className="font-semibold">{form.phone}</span> to confirm your visit.
                </p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <button onClick={resetForm}
                    className="px-6 py-3 bg-[#2e7d32] text-white font-semibold rounded-xl hover:bg-[#1b5e20] transition-colors">
                    Book Another
                  </button>
                  <button onClick={() => navigate('/')}
                    className="px-6 py-3 border-2 border-[#2e7d32] text-[#2e7d32] font-semibold rounded-xl hover:bg-green-50 transition-colors">
                    Back to Home
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
